/* Единственный способ, которым кабинет ходит в сеть.

   ТРИ СЕРВИСА ЗА ОДНИМ ГЕЙТВЕЕМ. Кабинету нужны user-api (личность и профиль),
   post-care-api (планы ухода) и appointment-api (визиты, когда появится ручка).
   Гейтвей адресует апстрим ПЕРВЫМ СЕГМЕНТОМ пути (`<сервис>.application/…`),
   поэтому адрес соседа получается подменой этого сегмента в базе — тот же приём,
   что на публичной странице врача.

   ВСЁ ЗДЕСЬ ИДЁТ С КУКОЙ. Кабинет — закрытый раздел: ни одной анонимной ручки в
   нём нет, личность берёт гейтвей из куки `access_token`. Поэтому
   `credentials: 'include'` стоит в единственной обёртке, а не выбирается по
   месту: забыть его — значит получить 401 там, где данные есть.

   `type-request: internal` обязателен: без него гейтвей не проксирует, а
   обслуживает путь сам и отвечает 404 — симптом обманчивый. Ценой идёт preflight
   OPTIONS перед каждым запросом; для закрытого раздела это не жалко.

   ⚠️ CORS. Ответ с кукой браузер примет только если сервер вернул КОНКРЕТНЫЙ
   origin. При `allow_origins: ["*"]` на гейтвее вместе с `allow_credentials:
   true` браузер отвергает ответ целиком, и из JS это неотличимо от обрыва сети.
   Значит адрес кабинета обязан стоять в `cors.origins` гейтвея — это условие
   работы, а не улучшение. */

/** Адрес ГЕЙТВЕЯ, и только он: `https://gateway.dev.aventrae.net`.
 *  Пусто — кабинет работает без сети, разделы показывают «не подключено».
 *
 *  ИМЁН СЕРВИСОВ В ПЕРЕМЕННОЙ НЕТ, и это решение человека 13.09. Раньше база
 *  включала путь до одного из сервисов (`…/user-api.application/api/v1`), а
 *  адреса соседей выводились подменой первого сегмента. Работало, но держало
 *  ЗНАНИЕ О СОСТАВЕ ФЛОТА в переменной окружения: чтобы понять, куда ходит
 *  кабинет, надо было прочитать значение из настроек репозитория, а не код.
 *  Теперь наоборот — в переменной адрес, в коде состав. */
export const API_BASE: string = import.meta.env.VITE_API_BASE?.replace(/\/+$/, '') ?? '';

/** База сервиса за гейтвеем. Первый сегмент пути — имя сервиса: так и только так
 *  гейтвей адресует апстрим (`GatewayMiddleware` строит адрес из самого пути). */
export function serviceBase(service: string): string | null {
    if (!API_BASE) return null;
    return `${API_BASE}/${service}.application/api/v1`;
}

/** Корень гейтвея — на нём живут ЕГО СОБСТВЕННЫЕ ручки входа и выхода. Они лежат
 *  не под `<сервис>.application/`, поэтому адрес берётся как есть. */
export function gatewayRoot(): string | null {
    return API_BASE || null;
}

/** Адрес публичных страниц платформы, или null — если `VITE_PLATFORM_HOST` не
 *  задан. Туда ведут «записаться» и «найти врача»: записи кабинет не делает, её
 *  делает страница врача.
 *
 *  ЗАПАСНОГО ЗНАЧЕНИЯ НЕТ (решение человека 13.09). Прод-адрес «на случай пустой
 *  переменной» означал бы, что дев-сборка молча уводит пациента на боевой сайт:
 *  сборка прошла, под зелёный, ссылка не та. Нет адреса — нет и кнопки.
 *
 *  ОТДЕЛЬНАЯ ПЕРЕМЕННАЯ, А НЕ `VITE_PUBLIC_HOST`. Во флоте `VITE_PUBLIC_HOST`
 *  означает хост, на котором стоит САМО приложение, — он дублирует
 *  `ingress.hosts[0].host` того же сервиса. У страницы врача её собственный хост
 *  и хост публичных страниц — одна строка, поэтому подмена смысла там
 *  незаметна; у кабинета это разные домены.
 *
 *  Значение — ХОСТ БЕЗ СХЕМЫ, схему добавляем сами. Берётся первая строка и
 *  снимается лишнее: в соседнем репозитории уже был случай, когда в переменную
 *  скопировали две строки из `.env.example` разом. */
export function platformSite(): string | null {
    const host = (import.meta.env.VITE_PLATFORM_HOST || '')
        .split('\n')[0]
        .trim()
        .replace(/^https?:\/\//i, '')
        .replace(/^\/+|\/+$/g, '');
    return /^[a-z0-9.-]+$/i.test(host) && host.includes('.') ? `https://${host}` : null;
}

/** Запрос к сервису ЗА гейтвеем: с кукой и с маркером проксирования. */
export function apiFetch(base: string, path: string, init: RequestInit = {}): Promise<Response> {
    return fetch(`${base}${path}`, {
        ...init,
        credentials: 'include',
        headers: {
            Accept: 'application/json',
            // Маркер проксирования нужен ВСЕГДА: у кабинета нет режима прямого
            // обращения в сервис — без гейтвея ему не взять ни куку, ни соседей.
            'type-request': 'internal',
            ...(init.headers as Record<string, string> | undefined),
        },
    });
}

/** Запрос к СОБСТВЕННОЙ ручке гейтвея (`/login/link`, `/logout`): с кукой, но
 *  БЕЗ маркера проксирования — с ним гейтвей попытается проксировать дальше и
 *  ответит 404. */
export function gatewayFetch(path: string, init: RequestInit = {}): Promise<Response> {
    const root = gatewayRoot();
    if (root === null) return Promise.reject(new Error('нет адреса гейтвея'));
    return fetch(`${root}/api/v1${path}`, {
        ...init,
        credentials: 'include',
        headers: { Accept: 'application/json', ...(init.headers as Record<string, string> | undefined) },
    });
}

/** Причина отказа СЛОВАМИ СЕРВЕРА. Конверт ошибок у бэкенда один на все сервисы:
 *  `{ typeError, timeCreateError, errorMessage }`. Свой текст поверх скрыл бы
 *  единственное, что объясняет отказ — например «заполните профиль». */
export async function serverMessage(res: Response, fallback: string): Promise<string> {
    try {
        const body = await res.json();
        const message = typeof body?.errorMessage === 'string' ? body.errorMessage.trim() : '';
        return message || fallback;
    } catch {
        return fallback;
    }
}
